/** Экран метода — SPEC-2 §4.1. */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { Applications, Methods, Tasks } from "@/api/resources";
import type { MethodDetailOut, MethodLinkKind, MethodVersionOut } from "@/api/types";
import { ConflictSheet } from "@/components/ConflictSheet";
import { MethodExperienceLine, MethodStatusBadge } from "@/components/MethodBadges";
import { VersionDiff, VersionSections } from "@/components/VersionDiff";
import { Badge, FilterChip } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { TextArea } from "@/components/ui/Field";
import { ScreenTitle, Section } from "@/components/ui/Section";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonCard } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { formatDayMonth, parseDate } from "@/lib/format";
import { nameOf, usePeople } from "@/lib/people";
import { useBackButton, useMainButton } from "@/lib/telegram-ui";
import { useVersionedUpdate } from "@/lib/useVersionedUpdate";

type Tab = "instruction" | "versions" | "applications" | "sources";

const TABS: { value: Tab; label: string }[] = [
  { value: "instruction", label: "Инструкция" },
  { value: "versions", label: "Версии" },
  { value: "applications", label: "Применения" },
  { value: "sources", label: "Источники" },
];

const LINK_LABEL: Record<MethodLinkKind, string> = {
  idea: "идея",
  work_log_entry: "запись журнала",
  tool: "инструмент",
  attachment: "вложение",
  example_task: "задача-пример",
  material: "материал",
};

export function MethodDetailView() {
  const { method_id } = useParams<{ method_id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const people = usePeople();
  const [tab, setTab] = useState<Tab>("instruction");
  const [applying, setApplying] = useState(false);
  const [improving, setImproving] = useState(false);
  const [diffAgainst, setDiffAgainst] = useState<MethodVersionOut | null>(null);

  // Nested screen: Telegram's own back, falling back to the tab when the
  // method was opened by a direct link and the history stack is empty.
  useBackButton("/knowledge");

  const method = useQuery({
    queryKey: ["method", method_id],
    queryFn: () => Methods.get(method_id!),
    enabled: Boolean(method_id),
  });

  const m = method.data;

  const statusUpdate = useVersionedUpdate<{ status: MethodDetailOut["status"] }, MethodDetailOut>({
    version: m?.version,
    mutate: (body) => Methods.setStatus(method_id!, body.status, body.version),
    invalidate: [["method", method_id], ["methods"]],
    onSuccess: () => toast.success("Статус обновлён"),
    onError: (e) => toast.error(e.message),
  });

  const currentUpdate = useVersionedUpdate<{ version_id: string }, MethodDetailOut>({
    version: m?.version,
    mutate: (body) => Methods.setCurrentVersion(method_id!, body.version_id, body.version),
    invalidate: [["method", method_id], ["methods"]],
    onSuccess: () => toast.success("Текущая версия обновлена"),
    onError: (e) => toast.error(e.message),
  });

  const successCount = m?.proven_by.length ?? 0;
  const canProve = successCount > 0;

  /**
   * The MainButton reads `proven_by`, it does not guess.
   *
   * The server refuses `→ proven` with 409 when no application succeeded. A
   * button that offered the transition anyway would be lying to the person
   * pressing it, so it is disabled with the reason spelled out — which is also
   * the most useful thing the screen can say about what `proven` means.
   */
  useMainButton(
    m?.status === "draft" || m?.status === "needs_review"
      ? {
          text: canProve ? "Перевести в проверен" : "Нужно успешное применение",
          enabled: canProve,
          onClick: () => void statusUpdate.run({ status: "proven" }),
          progress: statusUpdate.pending,
        }
      : m
        ? {
            text: "Применить к задаче",
            enabled: true,
            onClick: () => setApplying(true),
            progress: false,
          }
        : null,
  );

  const current = useMemo(
    () => m?.versions.find((v) => v.id === m.current_version_id) ?? m?.versions.at(-1) ?? null,
    [m],
  );

  if (!method_id) return null;
  if (method.isLoading) return <SkeletonCard />;
  if (method.isError || !m) {
    return (
      <Card className="space-y-2">
        <p className="text-cell text-text">Метод не найден.</p>
        <Button size="compact" variant="secondary" onClick={() => void method.refetch()}>
          Повторить
        </Button>
      </Card>
    );
  }

  const applications = m.applications;
  const successes = applications.filter((a) => a.outcome === "success").length;

  return (
    <div className="space-y-4 pt-2">
      <ScreenTitle
        eyebrow={nameOf(people.data, m.author_id) ?? undefined}
        trailing={<MethodStatusBadge status={m.status} />}
      >
        {m.title}
      </ScreenTitle>

      {m.summary && <p className="text-cell text-hint">{m.summary}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {m.tags.map((t) => (
          <span
            key={t}
            className="inline-flex h-5 items-center rounded-badge border border-sep px-1.5 text-micro text-hint"
          >
            {t}
          </span>
        ))}
      </div>

      <MethodExperienceLine
        versionNumber={current?.number}
        applications={applications.length}
        success={successes}
      />

      {m.status === "proven" && m.proven_by.length > 0 && (
        <Card className="space-y-1">
          <p className="text-meta text-hint">Подтверждён применением:</p>
          {m.proven_by.map((p) => (
            <Link
              key={p.application_id}
              to={`/tasks/${p.task_id}`}
              className="block text-cell text-accent"
            >
              задача {p.task_id.slice(0, 8)}… · доказательств: {p.evidence_entry_ids.length}
            </Link>
          ))}
        </Card>
      )}

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {TABS.map(({ value, label }) => (
          <FilterChip key={value} active={tab === value} onClick={() => setTab(value)}>
            {label}
          </FilterChip>
        ))}
      </div>

      {tab === "instruction" &&
        (current ? (
          <VersionSections version={current} />
        ) : (
          <EmptyState title="Версий пока нет" description="Метод создан без текста." />
        ))}

      {tab === "versions" && (
        <Section>
          {[...m.versions].reverse().map((version, index, list) => {
            const previous = list[index + 1];
            const isCurrent = version.id === m.current_version_id;
            return (
              <Card key={version.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="flex-1 text-cell font-medium text-text">v{version.number}</p>
                  {isCurrent && <Badge tone="accent">текущая</Badge>}
                </div>
                <p className="text-meta text-hint">
                  {nameOf(people.data, version.author_id) ?? "—"}
                  {parseDate(version.created_at)
                    ? ` · ${formatDayMonth(parseDate(version.created_at)!)}`
                    : ""}
                </p>
                {version.change_note && (
                  <p className="text-cell text-text">{version.change_note}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {previous && (
                    <Button
                      size="compact"
                      variant="secondary"
                      onClick={() => setDiffAgainst(version)}
                    >
                      Что изменилось
                    </Button>
                  )}
                  {!isCurrent && (
                    <Button
                      size="compact"
                      variant="ghost"
                      loading={currentUpdate.pending}
                      onClick={() => void currentUpdate.run({ version_id: version.id })}
                    >
                      Сделать текущей
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </Section>
      )}

      {tab === "applications" && (
        <Section>
          {applications.length === 0 && (
            <EmptyState
              title="Метод ещё не применяли"
              description="Пока его не применили, он остаётся черновиком — доказать метод может только использование."
            />
          )}
          {applications.map((a) => (
            <Card key={a.id} className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge
                  tone={
                    a.outcome === "success"
                      ? "accent"
                      : a.outcome === "failed"
                        ? "danger"
                        : "neutral"
                  }
                >
                  {a.outcome}
                </Badge>
                <span className="text-meta text-hint">
                  v{m.versions.find((v) => v.id === a.version_id)?.number ?? "?"}
                </span>
              </div>
              {a.result_md && <p className="text-cell text-text">{a.result_md}</p>}
              {a.limitations_md && (
                <p className="text-meta text-hint">Ограничения: {a.limitations_md}</p>
              )}
              <Link to={`/tasks/${a.task_id}`} className="text-meta text-accent">
                Открыть задачу
              </Link>
            </Card>
          ))}
        </Section>
      )}

      {tab === "sources" && (
        <Section>
          {m.links.length === 0 && (
            <EmptyState
              title="Источников нет"
              description="Метод не связан ни с задачей, ни с записями журнала — проверить его происхождение не по чему."
            />
          )}
          {m.links.map((link) => (
            <Card key={link.id} className="flex items-center gap-2">
              <Badge tone="neutral">{LINK_LABEL[link.kind]}</Badge>
              <span className="min-w-0 flex-1 truncate text-cell text-text">
                {link.label ?? link.target_id.slice(0, 8)}
              </span>
              {link.kind === "example_task" && (
                <Link to={`/tasks/${link.target_id}`} className="shrink-0 text-meta text-accent">
                  Открыть
                </Link>
              )}
              {(["idea", "material", "work_log_entry"] as string[]).includes(link.kind) && (
                <Link to={`/knowledge/sources/${link.kind === "material" ? "note" : link.kind}/${link.target_id}`} className="inline-flex min-h-touch items-center text-meta text-accent">
                  Открыть
                </Link>
              )}
            </Card>
          ))}
        </Section>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        <Button size="compact" variant="ghost" onClick={() => setImproving(true)}>
          Дополнить (новая версия)
        </Button>
        {m.status !== "needs_review" && (
          <Button
            size="compact"
            variant="ghost"
            loading={statusUpdate.pending}
            onClick={() => void statusUpdate.run({ status: "needs_review" })}
          >
            Требует пересмотра
          </Button>
        )}
        {m.status !== "archived" && (
          <Button
            size="compact"
            variant="ghost"
            loading={statusUpdate.pending}
            onClick={() => void statusUpdate.run({ status: "archived" })}
          >
            В архив
          </Button>
        )}
      </div>

      {diffAgainst && (
        <Sheet open onClose={() => setDiffAgainst(null)} title="Что изменилось">
          <VersionDiff
            before={
              m.versions.find((v) => v.number === diffAgainst.number - 1) ?? diffAgainst
            }
            after={diffAgainst}
          />
        </Sheet>
      )}

      <ApplyToTaskSheet
        open={applying}
        methodId={m.id}
        onClose={() => setApplying(false)}
        onApplied={() => {
          setApplying(false);
          qc.invalidateQueries({ queryKey: ["method", method_id] });
          toast.success("Применение начато");
        }}
      />

      <NewVersionSheet
        open={improving}
        method={m}
        onClose={() => setImproving(false)}
        onSaved={() => {
          setImproving(false);
          qc.invalidateQueries({ queryKey: ["method", method_id] });
          toast.success("Версия добавлена");
        }}
      />

      <ConflictSheet
        conflict={statusUpdate.conflict ?? currentUpdate.conflict}
        attempted={statusUpdate.attempted ?? currentUpdate.attempted}
        onReload={() => {
          statusUpdate.reload();
          currentUpdate.reload();
        }}
        onCancel={() => {
          statusUpdate.dismiss();
          currentUpdate.dismiss();
        }}
      />
    </div>
  );
}

function ApplyToTaskSheet({
  open,
  methodId,
  onClose,
  onApplied,
}: {
  open: boolean;
  methodId: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const toast = useToast();
  const tasks = useQuery({
    queryKey: ["tasks", "active"],
    queryFn: () => Tasks.listActive(25),
    enabled: open,
  });
  const start = useMutation({
    mutationFn: (taskId: string) => Applications.start(taskId, { method_id: methodId }),
    onSuccess: onApplied,
    onError: (e: Error) => toast.error(e.message),
  });

  if (!open) return null;
  return (
    <Sheet open onClose={onClose} title="К какой задаче применить">
      <div className="space-y-2">
        {tasks.isLoading && <SkeletonCard />}
        {tasks.data?.length === 0 && (
          <p className="text-meta text-hint">Активных задач нет.</p>
        )}
        {(tasks.data ?? []).map((task) => (
          <Button
            key={task.id}
            variant="secondary"
            block
            loading={start.isPending}
            onClick={() => start.mutate(task.id)}
          >
            {task.title}
          </Button>
        ))}
      </div>
    </Sheet>
  );
}

/**
 * A new version, never an edit of an old one.
 *
 * `set_current` is deliberately absent from this sheet: promoting a revision is
 * its own decision, made from the Версии tab where the person can see what they
 * are replacing. Bundling it here would make "write down what I learned" and
 * "change what everyone reads next" the same tap.
 */
function NewVersionSheet({
  open,
  method,
  onClose,
  onSaved,
}: {
  open: boolean;
  method: MethodDetailOut;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const current = method.versions.find((v) => v.id === method.current_version_id);
  const [body, setBody] = useState("");
  const [note, setNote] = useState("");
  const [seeded, setSeeded] = useState(false);

  if (open && !seeded) {
    setSeeded(true);
    setBody(current?.body_md ?? "");
  }
  if (!open && seeded) setSeeded(false);

  const save = useMutation({
    mutationFn: () =>
      Methods.addVersion(method.id, {
        content: {
          body_md: body,
          preconditions_md: current?.preconditions_md ?? null,
          checks_md: current?.checks_md ?? null,
          pitfalls_md: current?.pitfalls_md ?? null,
        },
        change_note: note.trim() || null,
        based_on_version_id: current?.id ?? null,
        set_current: false,
      }),
    onSuccess: onSaved,
    onError: (e: Error) => toast.error(e.message),
  });

  if (!open) return null;
  return (
    <Sheet
      open
      onClose={onClose}
      title="Новая версия"
      footer={
        <Button
          variant="strong"
          block
          loading={save.isPending}
          loadingText="Сохраняем…"
          onClick={() => body.trim() && save.mutate()}
        >
          Добавить версию
        </Button>
      }
    >
      <div className="space-y-3">
        <TextArea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Шаги"
          rows={8}
        />
        <TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Что изменилось против прошлой версии"
          rows={3}
        />
        <p className="text-meta text-hint">
          Старые версии не редактируются и не удаляются. Текущей эта не станет — это
          отдельное решение на вкладке «Версии».
        </p>
      </div>
    </Sheet>
  );
}
